/**
 * Headless presentation helpers for estimates — the labels, tones and
 * derivations every estimate surface needs (list card, detail modal, the
 * canonical detail page), kept platform-neutral so each shell renders the
 * same words.
 *
 * Icon and colour-class resolution stays in the shell: a tone here is a
 * semantic name ("error"), never a class name.
 */

import type {
  EstimateCarriageFinancials,
  EstimateCustomerAddress,
  EstimateCustomerContact,
  EstimateDelivery,
  EstimateDeliveryAddress,
  EstimateExpiry,
  EstimateInformation,
  EstimateNote,
  EstimateOrigin,
  EstimatePart,
  EstimateSpecification,
  EstimateTharsternFinancials,
  EstimateViewItem,
} from "../data/api-types";

/** Semantic tone for a badge/banner. Shells map these onto their own palette. */
export type EstimateTone = "success" | "warning" | "error" | "neutral";

// ── Commercial status (the estimate's own state in Tharstern) ────────────────

/** The six-value commercial status carried by every estimate read surface. */
export type EstimateStatus = EstimateViewItem["status"];

// Records rather than switches: a new union member becomes a compile error
// here, not a blank badge at runtime.
const STATUS_LABELS: Record<EstimateStatus, string> = {
  open: "Open",
  won: "Won",
  lost: "Lost",
  superseded: "Superseded",
  stale: "Stale",
  tender: "Tender",
};

const STATUS_TONES: Record<EstimateStatus, EstimateTone> = {
  open: "warning",
  won: "success",
  lost: "error",
  superseded: "neutral",
  stale: "neutral",
  tender: "neutral",
};

/** Human label for an estimate's commercial status. */
export function estimateStatusLabel(status: EstimateStatus): string {
  return STATUS_LABELS[status];
}

/** Badge tone for an estimate's commercial status. */
export function estimateStatusTone(status: EstimateStatus): EstimateTone {
  return STATUS_TONES[status];
}

// ── Contribution ────────────────────────────────────────────────────────────

/**
 * Contribution as a percentage of the estimate value, or `null` when there is
 * no value to divide by. The visible label on these surfaces stays "Margin"
 * (a product call); the domain concept is contribution.
 *
 * Zero-guarded: a £0.00 estimate is real (a placeholder, a cancelled line) and
 * `0 / 0` renders as "NaN%" if you let it through.
 */
export function contributionPercent(
  estimateAmt: number,
  contributionAmt: number,
): number | null {
  if (estimateAmt === 0) return null;
  return (contributionAmt / estimateAmt) * 100;
}

// ── Estimate detail: coverage-aware shaping ─────────────────────────────────
//
// Everything below serves the one internal estimate detail page. The single
// rule the whole section exists to enforce: a figure the system does not hold
// is **never** shaped into a row. A row with `pence: null` would be rendered
// by some caller as "£0.00" or "—" sooner or later, and on a pricing surface
// a fabricated zero reads as a fact. So a missing figure produces no row at
// all, and the block's `coverage` discriminator carries an explanation the
// page renders instead. See the module docstring of
// `views_module/api/schemas/estimates.py`.

/** How a money row sits in a build-up, so the shell can style it. */
export type MoneyRowKind = "line" | "subtotal" | "uplift" | "total" | "aside";

/**
 * One row of a money build-up. `pence` is non-nullable on purpose — see the
 * section note above. `pct` is a percentage that belongs *to this row*
 * (a markup rate, a contribution rate), not a share of some other total.
 */
export interface MoneyRow {
  key: string;
  label: string;
  pence: number;
  kind: MoneyRowKind;
  pct?: number | null;
  /** A free-text per-line annotation (e.g. "+15%", "−5.0%") — distinct from
   * `pct`, which is a computed percentage the caller derives; `note` is
   * carried verbatim from the source line (see `EstimatePolicyLine.note`). */
  note?: string | null;
}

/** Push a row only when the amount is a real figure. */
function pushMoneyRow(
  rows: MoneyRow[],
  key: string,
  label: string,
  pence: number | null | undefined,
  kind: MoneyRowKind,
  pct?: number | null,
): void {
  if (pence == null) return;
  rows.push({ key, label, pence, kind, pct });
}

// ── Tharstern's own cost → markup → price ladder ────────────────────────────

export type TharsternLadder =
  | { kind: "full"; rows: MoneyRow[]; asides: MoneyRow[] }
  | {
      kind: "totals_only";
      rows: MoneyRow[];
      asides: MoneyRow[];
      explanation: string;
    }
  | { kind: "unavailable"; explanation: string };

const THARSTERN_TOTALS_ONLY_EXPLANATION =
  "Tharstern has replicated this estimate's totals but not the cost lines " +
  "behind them. The cost sync covers a rolling 90-day window, so older " +
  "estimates keep their totals and fill in the build-up on the next full " +
  "refresh.";

const THARSTERN_UNAVAILABLE_EXPLANATION =
  "Tharstern holds no cost record for this estimate, so there is no " +
  "build-up to show.";

/**
 * Tharstern's build-up as an ordered set of rows, plus the standalone figures
 * that are not part of the ladder ("asides").
 *
 * `rows` is the ladder proper: each cost line, the total those lines add to,
 * the markup applied on top, and the price that comes out. `asides` are the
 * figures Tharstern reports alongside it — contribution, direct cost, agency
 * commission — which explain the same price on a different basis and would
 * double-count if they were stacked into the ladder.
 *
 * `origination_labour_only_pence` is deliberately dropped: it is the
 * alternative origination basis `direct_cost` uses, so showing it next to
 * `origination_labour` would present two competing figures for one cost.
 */
export function tharsternCostLadder(
  tharstern: EstimateTharsternFinancials,
): TharsternLadder {
  if (tharstern.coverage === "unavailable") {
    return {
      kind: "unavailable",
      explanation: THARSTERN_UNAVAILABLE_EXPLANATION,
    };
  }

  const rows: MoneyRow[] = [];
  for (const line of tharstern.cost_lines ?? []) {
    rows.push({
      key: line.key,
      label: line.label,
      pence: line.amount_pence,
      kind: "line",
    });
  }
  pushMoneyRow(
    rows,
    "cost_lines_total",
    "Cost build-up total",
    tharstern.cost_lines_total_pence,
    "subtotal",
  );
  pushMoneyRow(
    rows,
    "markup",
    "Markup applied by Tharstern",
    tharstern.markup_pence,
    "uplift",
    tharstern.markup_pct,
  );
  pushMoneyRow(
    rows,
    "price",
    "Tharstern price",
    tharstern.price_pence,
    "total",
  );

  const asides: MoneyRow[] = [];
  pushMoneyRow(
    asides,
    "cost_subtotal",
    "Cost subtotal (as reported)",
    tharstern.cost_subtotal_pence,
    "aside",
  );
  pushMoneyRow(
    asides,
    "direct_cost",
    "Direct cost",
    tharstern.direct_cost_pence,
    "aside",
  );
  // Contribution is deliberately NOT an aside, even though the response
  // carries it. It is the same number as the panel's own Contribution/Margin
  // figure, not a second opinion on it: the mart's `contribution_amt` and this
  // `contribution_pence` both derive from the replica's one `contribution`
  // column (fct_estimate <- int_est__base <- src_hist__estimate_details). The
  // only way they can differ is staleness — the mart rebuilds hourly, the
  // replica syncs every 15 minutes — so printing both invites a CSR to ask
  // which is right when the honest answer is "they are the same figure".
  pushMoneyRow(
    asides,
    "agency_commission",
    "Agency commission",
    tharstern.agency_commission_pence,
    "aside",
  );

  if (tharstern.coverage === "totals_only") {
    return {
      kind: "totals_only",
      rows,
      asides,
      explanation: THARSTERN_TOTALS_ONLY_EXPLANATION,
    };
  }
  return { kind: "full", rows, asides };
}

// ── Carriage on the estimate detail ─────────────────────────────────────────

export type EstimateCarriageDetail =
  | {
      kind: "cost_only";
      rows: MoneyRow[];
      isManual: boolean | null;
      explanation: string;
    }
  | { kind: "not_captured"; explanation: string };

const CARRIAGE_COST_ONLY_EXPLANATION =
  "Carriage was not charged to the customer as its own line. Tharstern " +
  "recovers it inside the quoted price, so what follows is cost, not a " +
  "charge.";

const CARRIAGE_NOT_CAPTURED_EXPLANATION =
  "No carriage figure was recorded for this estimate — neither a charge to " +
  "the customer nor a cost. That is not the same as free delivery.";

/**
 * The carriage Tharstern costed into the price, or why there is none.
 *
 * The rule is `!= null`, never truthiness: a null means nothing was ever
 * costed and must not render as £0.00.
 */
export function estimateCarriageDetail(
  carriage: EstimateCarriageFinancials,
): EstimateCarriageDetail {
  if (carriage.status === "not_captured") {
    return {
      kind: "not_captured",
      explanation: CARRIAGE_NOT_CAPTURED_EXPLANATION,
    };
  }

  const rows: MoneyRow[] = [];
  pushMoneyRow(
    rows,
    "tharstern_cost",
    "Tharstern's carriage cost",
    carriage.tharstern_cost_pence,
    "line",
  );
  const isManual = carriage.is_manual ?? null;
  return {
    kind: "cost_only",
    rows,
    isManual,
    explanation: CARRIAGE_COST_ONLY_EXPLANATION,
  };
}

// ── Where the quoted total came from ────────────────────────────────────────

/** Attribution for the customer-facing quoted total. */
export function quotedTotalSourceLabel(
  source: "tharstern" | "unknown",
): string {
  switch (source) {
    case "tharstern":
      return "Tharstern's own total for this estimate";
    case "unknown":
      return "No source recorded for this total";
  }
}

// ── Origin ──────────────────────────────────────────────────────────────────

export interface EstimateOriginPresentation {
  /** Human sentence for where the estimate came from. */
  label: string;
  /** True when the origin could not be resolved at all (memory/dev path). */
  isUnresolved: boolean;
}

// Keyed off the generated union rather than a hand-written one, so a fourth
// origin kind on the backend is a type error here rather than an `undefined`
// label at runtime.
const ORIGIN_KIND_LABELS: Record<
  NonNullable<EstimateOrigin["kind"]>,
  string
> = {
  cis_estimating: "Built in One's estimating tool",
  tharstern_native: "Created in Tharstern",
  draft: "Merchandise request",
};

/** How the estimate got here. */
export function estimateOriginPresentation(
  origin: EstimateOrigin,
): EstimateOriginPresentation {
  if (origin.coverage === "unavailable") {
    return {
      label: "Where this estimate came from could not be determined",
      isUnresolved: true,
    };
  }
  const kind = origin.kind ?? null;
  const label =
    origin.label ?? (kind ? ORIGIN_KIND_LABELS[kind] : "Origin not recorded");
  return { label, isUnresolved: false };
}

// ── Attribution and provenance labels ──────────────────────────────────────

const CONTACT_SOURCE_LABELS: Record<
  "estimate_free_text" | "customer_default",
  string
> = {
  // Tharstern's contact field is free text typed onto the estimate; email and
  // phone are recovered by matching that name against the customer's contacts.
  // A match is not a fact, and the label has to say so.
  estimate_free_text: "Name typed on the estimate; contact details matched",
  customer_default: "The customer's default contact",
};

/** Where the buyer's details on this estimate came from. */
export function contactSourceLabel(
  source: NonNullable<EstimateCustomerContact["source"]>,
): string {
  return CONTACT_SOURCE_LABELS[source];
}

const EXPIRY_SOURCE_LABELS: Record<EstimateExpiry["source"], string> = {
  tharstern_required_date: "Tharstern's required-by production date",
};

/** What the expiry date on this estimate actually measures. */
export function expirySourceLabel(source: EstimateExpiry["source"]): string {
  return EXPIRY_SOURCE_LABELS[source];
}

const NOTE_KIND_LABELS: Record<EstimateNote["kind"], string> = {
  tharstern_description: "Tharstern description",
};

/** Heading for one note on the estimate. */
export function noteKindLabel(kind: EstimateNote["kind"]): string {
  return NOTE_KIND_LABELS[kind];
}

const INFORMATION_COVERAGE_EXPLANATIONS: Record<
  EstimateInformation["coverage"],
  string | null
> = {
  full: null,
  pending_sync:
    "This estimate exists in Tharstern — it is created there first — but the " +
    "replica has not caught up yet. What is missing below is pending sync, " +
    "not empty.",
  unavailable:
    "No estimate record could be read for this estimate, so none of these " +
    "details are available.",
};

/** Why an information block is thin, or null when it is complete. */
export function informationCoverageExplanation(
  coverage: EstimateInformation["coverage"],
): string | null {
  return INFORMATION_COVERAGE_EXPLANATIONS[coverage];
}

const SPECIFICATION_COVERAGE_EXPLANATIONS: Record<
  EstimateSpecification["coverage"],
  string | null
> = {
  full: null,
  parts_pending_sync:
    "The parts of this estimate have not replicated yet. The catalogue " +
    "options below carry the specification in the meantime.",
  unavailable: "No specification could be read for this estimate.",
};

/** Why a specification block is thin, or null when it is complete. */
export function specificationCoverageExplanation(
  coverage: EstimateSpecification["coverage"],
): string | null {
  return SPECIFICATION_COVERAGE_EXPLANATIONS[coverage];
}

// ── Parts ───────────────────────────────────────────────────────────────────

/**
 * The descriptors of one estimate part, in reading order, with the unknown
 * ones left out rather than rendered as blanks.
 *
 * `orientation` is deliberately absent: the replica carries Tharstern's raw
 * bit without recording which way round it means, so a "Portrait" chip here
 * would be a guess presented as a fact.
 */
export function partDescriptors(part: EstimatePart): string[] {
  const descriptors: string[] = [];
  if (part.paper_ref) descriptors.push(part.paper_ref);
  if (part.finished_size_display) descriptors.push(part.finished_size_display);
  if (part.total_pages != null) descriptors.push(`${part.total_pages}pp`);
  if (part.press1) descriptors.push(part.press1);
  return descriptors;
}

// ── Delivery ────────────────────────────────────────────────────────────────

export type EstimateAddressPresentation =
  | {
      kind: "address";
      address: EstimateDeliveryAddress;
      /** Sentence naming whose address this is. Always rendered. */
      provenanceLabel: string;
    }
  | { kind: "absent"; explanation: string };

const ADDRESS_PROVENANCE_LABELS: Record<
  EstimateDelivery["address_provenance"],
  string
> = {
  estimate: "Chosen on this estimate",
  // The estimate-stage *destination* lives in a Tharstern table that is not
  // replicated, so every destination the page can show is a customer standing
  // default. Unlabelled, it reads as the estimator's choice. (The estimate's
  // own customer-address snapshot is per-estimate but is not a destination —
  // it is rendered separately, by `estimateCustomerAddressPresentation`.)
  customer_default:
    "The customer's default delivery address — not chosen on this estimate",
  unknown: "Source of this address not recorded",
};

const DELIVERY_ADDRESS_ABSENT_EXPLANATION =
  "No delivery address is held for this estimate. Tharstern does not " +
  "replicate the estimate-stage destination, and this customer has no " +
  "default delivery address on file.";

/**
 * The delivery address with its provenance, or an explanation of its absence.
 *
 * The provenance label is part of the value, not an optional extra: a customer
 * default rendered bare in an estimate's delivery slot states something the
 * estimate never said.
 */
export function deliveryAddressPresentation(
  delivery: EstimateDelivery,
): EstimateAddressPresentation {
  if (!delivery.address) {
    return { kind: "absent", explanation: DELIVERY_ADDRESS_ABSENT_EXPLANATION };
  }
  return {
    kind: "address",
    address: delivery.address,
    provenanceLabel: ADDRESS_PROVENANCE_LABELS[delivery.address_provenance],
  };
}

const COURIER_PROVENANCE_LABELS: Record<
  EstimateDelivery["courier_provenance"],
  string
> = {
  estimate: "Chosen on this estimate",
  customer_default:
    "The customer's default courier — not chosen on this estimate",
  unknown: "Source of this courier not recorded",
};

/** Whose choice the courier on this estimate was. */
export function courierProvenanceLabel(
  provenance: EstimateDelivery["courier_provenance"],
): string {
  return COURIER_PROVENANCE_LABELS[provenance];
}

/** One line summarising a courier and its service, or null when neither is held. */
export function courierSummary(
  courier: EstimateDelivery["courier"],
): string | null {
  if (!courier) return null;
  const parts = [
    courier.courier_description ?? courier.courier_code,
    courier.service_description,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" — ") : null;
}

/**
 * A postal address as its non-empty lines, in reading order.
 *
 * Structurally typed rather than tied to one address model, because the page
 * renders two genuinely different addresses (the delivery destination and the
 * estimate's own customer-address snapshot) whose only shared trait is being
 * postal. It reads whichever of the four parts a caller has.
 */
export function addressLines(address: {
  name?: string | null;
  address?: string | null;
  postcode?: string | null;
  country?: string | null;
}): string[] {
  return [
    address.name,
    address.address,
    address.postcode,
    address.country,
  ].filter((line): line is string => Boolean(line && line.trim()));
}

// ── The customer address recorded on the estimate ────────────────────────────

export type EstimateCustomerAddressPresentation =
  | {
      kind: "address";
      lines: string[];
      /** Sentence naming what this address is — and is not. Always rendered. */
      note: string;
    }
  | { kind: "absent"; explanation: string };

// Deliberately never "Deliver to" or "Invoice to". Tharstern records this as the
// customer's own address on the quote (`MainEstimateDetails.CustomerAddress`);
// the destination is a separate, unreplicated fact and the invoice-to address is
// held nowhere. On a print job, an account address presented as the destination
// ships the work to a head office that never ordered it.
const CUSTOMER_ADDRESS_NOTE =
  "The customer's address as recorded on this estimate — not a delivery " +
  "destination";

const CUSTOMER_ADDRESS_NOT_HELD =
  "No customer address is held on this estimate. Either Tharstern recorded " +
  "none, or the estimate predates this field being replicated — a full " +
  "refresh fills the older ones.";

const CUSTOMER_ADDRESS_UNAVAILABLE =
  "The Tharstern estimate record could not be read, so whether it carries a " +
  "customer address is unknown.";

const CUSTOMER_ADDRESS_EMPTY =
  "A customer address is recorded on this estimate but every line of it is " +
  "blank.";

/**
 * The estimate's own customer address, or an explanation of its absence.
 *
 * The three `status` values are three different facts and each gets its own
 * sentence: an address is held; none is held; or we could not look. Collapsing
 * the last two would let an unreadable record read as an empty one.
 */
export function estimateCustomerAddressPresentation(
  customerAddress: EstimateCustomerAddress,
): EstimateCustomerAddressPresentation {
  if (customerAddress.status === "unavailable") {
    return { kind: "absent", explanation: CUSTOMER_ADDRESS_UNAVAILABLE };
  }
  if (customerAddress.status === "not_held") {
    return { kind: "absent", explanation: CUSTOMER_ADDRESS_NOT_HELD };
  }
  const lines = addressLines(customerAddress);
  if (lines.length === 0) {
    return { kind: "absent", explanation: CUSTOMER_ADDRESS_EMPTY };
  }
  return { kind: "address", lines, note: CUSTOMER_ADDRESS_NOTE };
}

// ── Related job ─────────────────────────────────────────────────────────────

export type LinkedJobInvoicePresentation =
  { kind: "invoiced" } | { kind: "pending" };

/** The Invoice Status badge's state for a related job, from the job record's own `invoiced_at`. */
export function linkedJobInvoicePresentation(
  invoicedAt: string | null,
): LinkedJobInvoicePresentation {
  return invoicedAt ? { kind: "invoiced" } : { kind: "pending" };
}
